<template>
  <AccountContextWrapper>
    <template #default>
      <div class="row">
        <div class="col-sm-12">
          <div
            class="d-flex flex-wrap justify-content-center align-items-center px-3 py-2 border-bottom bg-white gap-3"
          >
            <h5 class="mb-0 h6 fw-bold">Transactions</h5>
            <BFormGroup
              id="input-group-filter-start-date"
              label="Start Date:"
              label-for="filter-start-date"
              class="floating-group"
            >
              <BFormInput
                id="filter-start-date"
                v-model="fltrStartDate"
                type="date"
                size="sm"
                style="width: 120px; font-size: 0.85rem"
                :class="{ 'is-invalid': fltrsDateError !== null }"
                @change="onDateChange"
              />
            </BFormGroup>
            <BFormGroup
              id="input-group-filter-end-date"
              label="End Date:"
              label-for="filter-end-date"
              class="floating-group"
            >
              <BFormInput
                id="filter-end-date"
                v-model="fltrEndDate"
                type="date"
                size="sm"
                style="width: 120px; font-size: 0.85rem"
                :class="{ 'is-invalid': fltrsDateError !== null }"
                @change="onDateChange"
              />
            </BFormGroup>
            <BFormGroup
              id="input-group-inst-name-uid-filter"
              label="Inst Name & UID:"
              label-for="institution-name-uid-filter"
              class="floating-group"
            >
              <BFormSelect
                id="institution-name-uid-filter"
                :modelValue="selectedInstitution"
                :options="institutionOpts"
                size="sm"
                style="font-size: 0.85rem"
                @update:modelValue="handleInstitutionFilterChange"
              />
              <h6
                class="mt-1 mb-0"
                v-for="inst in selectedInsts"
                :key="inst.uid"
              >
                <BBadge variant="dark"
                  >{{ inst.name }} - {{ inst.acct_last_4
                  }}<IconX
                    size="13"
                    class="text-danger ms-1"
                    style="cursor: pointer"
                    @click="handleInstUnselection(inst)"
                /></BBadge>
              </h6>
            </BFormGroup>
            <IconRefresh
              size="16"
              class="text-success ms-2"
              style="cursor: pointer"
              @click="handleRefreshClick()"
            />
            <IconMoneybagPlus
              size="16"
              class="text-primary ms-2"
              style="cursor: pointer"
              @click="createModal = true"
            />
          </div>
        </div>
        <div class="col-sm-12 text-center">
          <span v-if="fltrsDateError" class="small text-danger">{{
            fltrsDateError
          }}</span>
        </div>
        <div class="col-sm-12 text-center">
          <ListView
            v-if="weeklyOutlooks"
            :loadingTransactions="loadingTransactions"
            :transactions="filteredTransactions"
            :acctUid="acctUid"
            :trsxnsStartDate="fltrStartDate"
            :trsxnsEndDate="fltrEndDate"
            @deleteTransaction="handleTransactionDeletion"
          />
        </div>
      </div>
    </template>
  </AccountContextWrapper>
  <CreateTransactionModal
    v-if="acctUid"
    v-model="createModal"
    :institutionOpts="institutionOpts"
    :acctUid="acctUid"
    @transactionCreated="fetchTransactionsByDateRange"
  />
</template>
<script>
import {
  BFormInput,
  BFormGroup,
  BFormSelect,
  BBadge,
} from "bootstrap-vue-next";
import ListView from "@/components/Transactions/ListView.vue";
import CreateTransactionModal from "@/components/Transactions/CreateTransactionModal.vue";
import AccountContextWrapper from "@/components/molecules/AccountContextWrapper.vue";
import {
  userAccount as userAcctNs,
  spendingCategory as spendingCategoryNs,
  weeklyOutlooks as weeklyOutlooksNs,
  bankingInstitutions as bankingInstitutionsNs,
} from "@/store/namespaces";
import { formatDateToyyyyMMdd } from "@/utils/utils.js";
import { IconRefresh, IconX, IconMoneybagPlus } from "@tabler/icons-vue";

export default {
  name: "TransactionsIndexView",
  components: {
    AccountContextWrapper,
    BFormInput,
    BFormGroup,
    ListView,
    IconRefresh,
    BFormSelect,
    BBadge,
    IconX,
    IconMoneybagPlus,
    CreateTransactionModal,
  },
  computed: {
    acctUid() {
      return this.$store.getters[`${userAcctNs}/getSelectedAcctUid`];
    },
    weeklyOutlooks() {
      return this.$store.getters[`${weeklyOutlooksNs}/getWeeklyOutlooks`];
    },
    bankingInstitutions() {
      return this.$store.getters[
        `${bankingInstitutionsNs}/getBankingInstitutions`
      ];
    },
    institutionOpts() {
      const opts = [{ value: null, text: "— Select —" }];

      if (!this.bankingInstitutions) return opts;

      const institutionOpts = this.bankingInstitutions.map((inst) => {
        const uidLast6 = inst.uid.slice(-6);
        return {
          value: inst,
          text: `${inst.name} - ${inst.acct_last_4} (${uidLast6})`,
        };
      });

      return opts.concat(institutionOpts);
    },
    filteredTransactions() {
      if (!this.transactions) return null;

      if (this.selectedInsts.length === 0) return this.transactions;

      const filtered = [];
      this.transactions.forEach((txn) => {
        const matchesSelectedInst = this.selectedInsts.some(
          (inst) => inst.uid === txn.institution_uid
        );
        if (matchesSelectedInst) {
          filtered.push(txn);
        }
      });

      return filtered;
    },
  },
  data() {
    return {
      createModal: false,
      selectedInstitution: null,
      selectedInsts: [],
      fltrStartDate: null,
      fltrEndDate: null,
      fltrsDateError: null,
      transactions: null,
      loadingTransactions: false,
      weeklyOutlookStartDate: null,
      weeklyOutlookEndDate: null,
    };
  },
  watch: {
    // Make sure to refetch transactions when the selected account changes
    acctUid: {
      immediate: true,
      handler(newAcctUid, oldAcctUid) {
        if (newAcctUid !== oldAcctUid) {
          this.fetchTransactionsByDateRange();
          this.fetchWeeklyOutlooks();
          this.fetchBankingInstitutions();
        }
      },
    },
  },
  created() {
    // Fetch spending categories on mount to ensure they are available for transaction assignment
    // Reminder: this method already handles caching internally, so we can call it without checking if we already have categories
    let params = { sort: "name:asc" };
    this.$store.dispatch(
      `${spendingCategoryNs}/fetchSpendingCategories`,
      params
    );

    // Initialize transactions date filters to last 7 days by default
    const today = new Date();
    let startDateObj = new Date(today);
    startDateObj.setDate(startDateObj.getDate() - 7);
    this.fltrEndDate = formatDateToyyyyMMdd(today, "-");
    this.fltrStartDate = formatDateToyyyyMMdd(startDateObj, "-");

    // Initialize weekly outlook fetch range to 45 days before and after today (90 day range) to ensure we have the relevant weekly outlooks for the transactions date filters
    startDateObj = new Date(today);
    startDateObj.setDate(startDateObj.getDate() - 45);
    const endDateObj = new Date(today);
    endDateObj.setDate(endDateObj.getDate() + 45);
    this.weeklyOutlookStartDate = formatDateToyyyyMMdd(startDateObj, "-");
    this.weeklyOutlookEndDate = formatDateToyyyyMMdd(endDateObj, "-");

    this.fetchTransactionsByDateRange();
  },
  methods: {
    async handleTransactionDeletion(transactionUid) {
      try {
        await this.$api.delete(
          `/banking-institutions/transactions/${transactionUid}`,
          {
            data: { acct_uid: this.acctUid },
          }
        );

        // Remove the deleted transaction from the local transactions list to immediately reflect the change in the UI without needing to refetch from the backend
        this.transactions = [
          ...this.transactions.filter(
            (txn) => txn.transaction_uid !== transactionUid
          ),
        ];

        this.$notify.notifySuccess("Transaction deleted successfully.");
      } catch (error) {
        console.error("Error deleting transaction:", error);
        this.$notify.notifyError(
          "Failed to delete transaction. Please try again."
        );
      }
    },
    fetchWeeklyOutlooks() {
      const params = {
        filter: [
          `acctUid:eq:${this.acctUid}`,
          `weeklyOutlook:gte:${this.weeklyOutlookStartDate}`,
          `weeklyOutlook:lte:${this.weeklyOutlookEndDate}`,
        ],
      };
      this.$store.dispatch(`${weeklyOutlooksNs}/fetchWeeklyOutlooks`, {
        qryParams: params,
      });
    },
    fetchBankingInstitutions() {
      const params = {
        filter: [`acctUid:eq:${this.acctUid}`],
      };
      this.$store.dispatch(
        `${bankingInstitutionsNs}/fetchBankingInstitutions`,
        {
          qryParams: params,
        }
      );
    },
    onDateChange() {
      this.fltrsDateError = null;
      this.fetchTransactionsByDateRange();
    },
    validateTransactionsDateFilters() {
      if (!this.fltrStartDate) {
        this.fltrsDateError = "Start date is required.";
        return false;
      }

      if (!this.fltrEndDate) {
        this.fltrsDateError = "End date is required.";
        return false;
      }

      const start = new Date(this.fltrStartDate);
      const end = new Date(this.fltrEndDate);
      const diffDays = (end - start) / (1000 * 60 * 60 * 24);

      if (diffDays < 0) {
        this.fltrsDateError = "End date cannot be before start date.";
        return false;
      }

      if (diffDays > 61) {
        this.fltrsDateError = "Date range cannot exceed 61 days.";
        return false;
      }

      this.fltrsDateError = null;
      return true;
    },
    async fetchTransactionsByDateRange() {
      if (!this.validateTransactionsDateFilters()) return;

      if (this.acctUid === null) {
        console.warn(
          "No account selected, cannot fetch transactions. Please select an account first."
        );
        return;
      }

      this.transactions = null; // Set to null to show loading state
      this.loadingTransactions = true;
      try {
        const response = await this.$api.get(
          "/banking-institutions/transactions",
          {
            params: {
              filter: [
                `acctUid:eq:${this.acctUid}`,
                `date:gte:${this.fltrStartDate}`,
                `date:lte:${this.fltrEndDate}`,
              ],
            },
          }
        );
        this.transactions = response.data.map((txn) => txn.item);
      } catch (error) {
        console.error("Error fetching transactions:", error);
      } finally {
        this.loadingTransactions = false;
      }
    },
    handleInstitutionFilterChange(instObj) {
      this.selectedInstitution = null;
      if (instObj === null) {
        this.selectedInsts = [];
        return;
      }

      if (this.selectedInsts.some((inst) => inst.uid === instObj.uid)) {
        // If already selected, early return
        return;
      }

      this.selectedInsts.push(instObj);
    },
    handleInstUnselection(instObj) {
      this.selectedInsts = this.selectedInsts.filter(
        (inst) => inst.uid !== instObj.uid
      );
    },
    handleRefreshClick() {
      this.selectedInsts = [];
      this.fetchTransactionsByDateRange();
      this.fetchWeeklyOutlooks();
      this.fetchBankingInstitutions();
    },
  },
};
</script>
